import { OrderState } from "../../../../../shared/types/OrderState";
import { OrderDetailsState } from "../controls/OrderDetails";
import { AssetType } from "../../../../../shared/types/AssetType";
import { dateTimeConversionService } from "../../../../../shared/services/DateTimeConversionService";
import { optionParametersSelectorManager } from "../../../utils/OptionParametersSelectorManager";
import {
    BlackScholesModelCalculationService
} from "../../../../../shared/services/BlackScholesModelCalculationService";
import { portfolioMappers } from "../../../../../shared/mappers/PortfolioMappers";
import { OptionInfoDto } from "../../../../../screens/options-table/types/OptionInfoDto";
import { assetService } from "../../../../../shared/services/AssetService";
import type { MarketDataDto } from "../../../../../shared/types/MarketDataDto";
import { ContractDetailsState } from "../controls/ContractDetails";
import { PricingDetailsState } from "../controls/PricingParameters";
import { Dayjs } from "dayjs";

class OrderManagerUtils {
    isOptionCanBeCancelled = (orderDetails: OrderDetailsState): boolean => orderDetails.exchangeOrderId !== null;
    
    isNewOrder = (orderDetails: OrderDetailsState): boolean => orderDetails.externalId === 0;
    
    isOriginalOrderArchived = (orderDetails: OrderDetailsState): boolean => orderDetails.state === OrderState.Archived;
    
    updatePricingParameters = (
        marketDataDto: MarketDataDto,
        contractDetails: ContractDetailsState,
        dateForPricing: Dayjs,
        impliedVolatilityForPricing: number,
        underlyingPriceForPricing: number,
        handlePricingDetailsChange: (patch: Partial<PricingDetailsState>) => void,
    ): void => {
        if (assetService.isOptionType(contractDetails.assetType)){
            const { price, delta, gamma, vega, theta } = oderManagerUtils.getOptionParameters(
                marketDataDto.OptionInfos, contractDetails.assetType, contractDetails.strike!,
                contractDetails.expiration, impliedVolatilityForPricing,
                underlyingPriceForPricing, dateForPricing.toDate());

            handlePricingDetailsChange({
                dateForPricing: dateForPricing,
                impliedVolatilityForPricing: impliedVolatilityForPricing,
                underlyingPriceForPricing: underlyingPriceForPricing,
                theoreticalPrice: price,
                delta: delta,
                gamma: gamma ?? 0,
                vega: vega ?? 0,
                theta: theta ?? 0,
            });
        } else {
            handlePricingDetailsChange({
                impliedVolatilityForPricing: 0,
                underlyingPriceForPricing: 0,
                theoreticalPrice: marketDataDto.UnderlyingPrice ?? 0,
                delta: 1,
                gamma: 0,
                vega: 0,
                theta: 0,
            });
        }
    }

    updatePricingParametersWithCurrentMarketValues = (
        marketDataDto: MarketDataDto,
        contractDetails: ContractDetailsState,
        handlePricingDetailsChange: (patch: Partial<PricingDetailsState>) => void,
    ): void => {
        if (assetService.isOptionType(contractDetails.assetType)){
            const optionInfoDto = optionParametersSelectorManager.getOption(
                marketDataDto!.OptionInfos, contractDetails.assetType,
                dateTimeConversionService.parseToUtcDate(contractDetails.expiration), contractDetails.strike!);
            
            const dateForPricing = dateTimeConversionService.convertToUtcDate(new Date());
            const impliedVolatilityForPricing = optionInfoDto?.MarkImpliedVolatility ?? 0;
            const underlyingPriceForPricing = marketDataDto!.UnderlyingPrice ?? 0;
            
            const { price, delta, gamma, vega, theta } = oderManagerUtils.getOptionParameters(
                marketDataDto.OptionInfos, contractDetails.assetType, contractDetails.strike!,
                contractDetails.expiration, impliedVolatilityForPricing,
                underlyingPriceForPricing, dateForPricing.toDate());

            handlePricingDetailsChange({
                dateForPricing: dateForPricing,
                impliedVolatilityForPricing: impliedVolatilityForPricing,
                underlyingPriceForPricing: underlyingPriceForPricing,
                theoreticalPrice: price,
                delta: delta,
                gamma: gamma ?? 0,
                vega: vega ?? 0,
                theta: theta ?? 0,
            });
        } else {
            handlePricingDetailsChange({
                impliedVolatilityForPricing: 0,
                underlyingPriceForPricing: 0,
                theoreticalPrice: marketDataDto.UnderlyingPrice ?? 0,
                delta: 1,
                gamma: 0,
                vega: 0,
                theta: 0,
            });
        }
    }
    
    getOptionParameters = (
        optionInfos: OptionInfoDto[],
        assetType: AssetType,
        strike: number,
        expiration: string,
        impliedVolatility: number,
        underlyingAssetPrice: number,
        dateTime: Date = new Date(),
    ): {
        price: number,
        delta: number,
        gamma: number | null,
        vega: number | null,
        theta: number | null,
        currentDateTime?: Date | null,
    } => {
        const parsedExpiration = dateTimeConversionService.parseToUtcDate(expiration);

        const optionDto = optionParametersSelectorManager.getOption(
            optionInfos, assetType, parsedExpiration, strike);

        const riskFreeInterest = optionDto?.RiskFreeInterest
            ?? BlackScholesModelCalculationService.riskFreeRate;

        const { price, delta, gamma, vega, theta } =
            portfolioMappers.getAssetParameters(assetType, strike,
                parsedExpiration, impliedVolatility, riskFreeInterest,
                underlyingAssetPrice, dateTime);

        return { price, delta, gamma, vega, theta };
    }
}
    
export const oderManagerUtils = new OrderManagerUtils();